import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FraudFlag, ResolveFraudFlag } from '@dcc-erp/shared';

import { api, apiJson, downloadFile } from '../api';

// M7 Phase 4 — read-only/INFO-only fraud-detective dashboard hooks. The flag feed
// + a manual scan + resolve/dismiss case-tracking actions + a per-report PDF.
// Cache-key scoped by companyId per queries/ar.ts / queries/ap.ts convention.
const flagsKey = (companyId: string | undefined) => ['fraud-flags', companyId] as const;

// READ — the dashboard case list (fraud.read).
export function useFraudFlags(
  companyId: string | undefined,
  filter: { status?: string; type?: string } = {},
) {
  const qs = new URLSearchParams();
  if (filter.status) qs.set('status', filter.status);
  if (filter.type) qs.set('type', filter.type);
  const search = qs.toString();
  return useQuery({
    queryKey: [...flagsKey(companyId), filter],
    queryFn: () =>
      api<FraudFlag[]>(`/companies/${companyId}/fraud/flags${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// Run all reports + upsert flags now (fraud.manage).
export function useRunFraudScan(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<undefined, { flagged: number }>('POST', `/companies/${companyId}/fraud/run`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: flagsKey(companyId) }),
  });
}

export function useResolveFraudFlag(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      apiJson<ResolveFraudFlag, FraudFlag>(
        'POST',
        `/companies/${companyId}/fraud/flags/${id}/resolve`,
        {
          comment,
        },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: flagsKey(companyId) }),
  });
}

export function useDismissFraudFlag(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      apiJson<ResolveFraudFlag, FraudFlag>(
        'POST',
        `/companies/${companyId}/fraud/flags/${id}/dismiss`,
        {
          comment,
        },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: flagsKey(companyId) }),
  });
}

// Authenticated PDF download of one detective report.
export function downloadFraudReportPdf(companyId: string, type: string) {
  return downloadFile(
    `/companies/${companyId}/fraud/reports/${type}/export.pdf`,
    `fraud-${type}.pdf`,
  );
}
